// src/config/metiers.config.ts — SCE Issues
// Configuration centrale des métiers SCE

import type { Metier } from '@/types/issue.types'

/**
 * Familles de problèmes par métier.
 * Utilisé pour filtrer dynamiquement le dropdown "Famille" dans le formulaire.
 *
 * ⚠️ Les familles MECA/PROCESS doivent être complétées avec Hugues
 * avant le Sprint 2.
 */
export const METIERS_FAMILLES: Record<Metier, string[]> = {
  ELEC: [
    'Absence de matériel',
    'Adresse PLC',
    'Calculs de boucles',
    'Configuration appareils',
    'Évolution matériel',
    'Raccordement — inversion de fils / mauvais câblage',
    'Implantation — élément manquant',
    'Implantation — ne se monte pas / non présent',
    'Référence matérielle',
    'Repérage',
    'Amélioration',
  ],
  // Liste propre à l'Autom, transmise le 23/10/2026 (avant : celle de l'Élec).
  // Les problèmes déjà qualifiés avec une famille Élec la gardent (Famille est
  // une colonne texte).
  AUTOM: [
    'HDS',
    'Référence',
    'Version',
    'PLC',
    'SCADA',
    'Alarmes',
    'Adressage',
    'Graphics',
  ],
  // Liste transmise le 24/09/2026. « Erreur conception mécanique », seule
  // valeur jusque-là, n'est plus proposée mais reste dans la liste de choix
  // SharePoint : les problèmes déjà qualifiés ainsi la gardent.
  MECA: [
    'Support manquant / mal défini',
    'Raccord manquant / mal défini',
    'Joint manquant / mal défini',
    'Fixation ou vis manquante / mal définie',
    'Interférence entre pièces',
    'Jeu ou frottement excessif',
    'Dimensions incorrectes sur plan',
    'Pièce ou composant absent ou mal positionné',
    'Matériau non conforme',
    'Trous manquants / mal positionnés',
    'Erreurs TAGS',
    'Article process mal placé',
    'Erreur tubing',
  ],
  PROCESS: [
    'Erreur conception process',
    // ⚠️ Familles critiques Hugues à compléter
  ],
}

/**
 * Couleur de chaque métier — TOUJOURS une variable CSS, jamais un hex : les
 * coordinateurs la changent au runtime (Lot 8, features/couleurs-metier).
 */
export const METIERS_COULEURS: Record<Metier, string> = {
  ELEC: 'var(--color-metier-elec)',
  AUTOM: 'var(--color-metier-autom)',
  MECA: 'var(--color-metier-meca)',
  PROCESS: 'var(--color-metier-process)',
}

/**
 * Couleur du texte posé SUR la couleur du métier (badge, puce sélectionnée).
 * Noir ou blanc selon la teinte choisie — jamais du blanc d'office : il est
 * illisible sur Orange, Teal ou Light Blue.
 */
export const METIERS_COULEURS_TEXTE: Record<Metier, string> = {
  ELEC: 'var(--color-metier-elec-texte)',
  AUTOM: 'var(--color-metier-autom-texte)',
  MECA: 'var(--color-metier-meca-texte)',
  PROCESS: 'var(--color-metier-process-texte)',
}

/**
 * Libellés affichés dans l'UI (peuvent différer du code interne).
 */
export const METIERS_LABELS: Record<Metier, string> = {
  ELEC: 'Électricité',
  AUTOM: 'Automatisme',
  MECA: 'Mécanique',
  PROCESS: 'Process',
}

/**
 * Libellé court pour les chips/badges compacts.
 */
export const METIERS_LABELS_COURT: Record<Metier, string> = {
  ELEC: 'ELEC',
  AUTOM: 'AUTOM',
  MECA: 'MECA',
  PROCESS: 'PROCESS',
}

/**
 * Liste ordonnée pour l'affichage des chips/filtres.
 */
export const METIERS_ORDRE: readonly Metier[] = [
  'ELEC',
  'AUTOM',
  'MECA',
  'PROCESS',
] as const
